import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { Creative } from '@feathr/blackbox';
import type { ITanStackColumnMeta } from '@feathr/components';
import { TruncatedTextCell } from '@feathr/components';

function formatDimensions(creative: Creative): string | undefined {
  const width = creative.get('width');
  const height = creative.get('height');
  return width && height ? `${width}px x ${height}px` : undefined;
}

/**
 * Column definitions for the creatives table in the campaign summary.
 *
 * Cell bodies read the model through `Observer` because `TanStackTable` invokes them inside its
 * own body, where the summary's `observer` does not track them.
 */
export default function creativesSummaryColumns(t: TFunction): Array<ColumnDef<Creative>> {
  return [
    {
      accessorFn: (row): string => row.name,
      cell: ({ row }: CellContext<Creative, unknown>): JSX.Element => (
        <Observer>{(): JSX.Element => <TruncatedTextCell value={row.original.name} />}</Observer>
      ),
      header: t('File Name'),
      id: 'name',
      meta: { isFlex: true, isPrimary: true } satisfies ITanStackColumnMeta,
    },
    {
      accessorFn: (row): string => formatDimensions(row) ?? '',
      cell: ({ row }: CellContext<Creative, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => <TruncatedTextCell value={formatDimensions(row.original)} />}
        </Observer>
      ),
      header: t('Dimensions'),
      id: 'dimensions',
    },
    {
      accessorFn: (row): string => row.getTypeLabel(),
      cell: ({ row }: CellContext<Creative, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => <TruncatedTextCell value={row.original.getTypeLabel()} />}
        </Observer>
      ),
      header: t('Type'),
      id: 'type',
    },
  ];
}
